import { useState, useRef, useMemo, useEffect } from 'react'
import { useTranslation } from 'next-i18next'

import ProgressBar, { ProgressBarSize } from '~/dashboard/components/ProgressBar'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import progressStyles from '~/bootcamp/components/StripeProgressBar/StripeProgressBar.module.css'
import { CheckListTypes } from '~/redux/apis/types'
import checklistAnalytics from '~/analytics/checklist/checklistAnalytics'
import checklistStyles from '~/checklist/styles/Checklist.module.css'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectOpenChecklist, setOpenChecklist } from '~/redux/slices/dashboard'

import { getChecklistProgress } from '../helper'

import ArrowButton from './ArrowButton'
import CheckListItemComponent from './CheckListItem'
import CompletedChest from './CompletedChest'

interface Props {
  checklist: CheckListTypes
  handleChestClick: () => void
}

export default function ChecklistContainer({ checklist, handleChestClick }: Props): React.ReactElement | null {
  const { t } = useTranslation('dashboard')
  const [expanded, setExpanded] = useState(false)
  const [height, setHeight] = useState(0)
  const dispatch = useAppDispatch()
  const isChecklistOpen = useAppSelector(selectOpenChecklist)

  const containerRef = useRef<HTMLDivElement>(null)
  const collapsibleRef = useRef<HTMLDivElement>(null)

  const calculateHeight = (): void => {
    const contentHeight = collapsibleRef.current?.scrollHeight
    const newHeight = expanded ? 0 : contentHeight // we are calling the function after reversing the expanded state
    setHeight(newHeight ?? 0)
  }

  const progress = useMemo(() => {
    const { total, completed } = getChecklistProgress(checklist)
    return (completed / total) * 100
  }, [checklist])

  const handleExpand = (): void => {
    if (!expanded) checklistAnalytics.logChecklistExpanded()
    setExpanded(!expanded)
    calculateHeight()
  }

  useEffect(() => {
    if (isChecklistOpen && !expanded) {
      setTimeout(() => {
        handleExpand()
        dispatch(setOpenChecklist(false))
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecklistOpen])

  return (
    <div className="relative lg:justify-center w-85 px-1 mt-4">
      <div className="border border-none rounded bg-gradient-to-b from-blue-logo via-pink-logo to-orange p-px md:mx-auto">
        <div className="flex flex-col justify-between border-none rounded bg-white" ref={containerRef}>
          <button
            className={`border-none rounded w-full bg-purple ${checklistStyles['tap-effect-none']}`}
            onClick={handleExpand}
          >
            <div className="flex flex-row items-center justify-between py-2 px-1.5 md:pr-2 space-x-2">
              <div className="h-9">
                <ResponsiveImage
                  alt="rocket"
                  height={16}
                  imageClassName="w-9 h-9"
                  srcKey="icons/checklist-rocket.png"
                  width={16}
                />
              </div>
              <div className="flex tracking-widest items-start mx-auto text-white font-bold leading-normal">
                <h2 className="text-center">{t('checklists.completeChecklist')}</h2>
              </div>
              <div className="md:pl-3">
                <ArrowButton color="text-white" expanded={expanded} />
              </div>
            </div>

            <div className="w-67 mx-auto mt-1 pb-5">
              <ProgressBar
                className={`bg-orange ${progressStyles['stripe-gradient']}`}
                progressPercent={progress}
                size={ProgressBarSize.MD}
              />
            </div>
          </button>
          <div
            className="opacity-100 overflow-hidden"
            ref={collapsibleRef}
            style={{
              transition: `height 200ms cubic-bezier(0.4, 0, 1, 1)`,
              height,
            }}
          >
            <div className="border-b border-b-purple-semi-light mx-2 pt-2.5 pb-1">
              {checklist &&
                Object.entries(checklist).map(([itemKey, checklistItem]) => (
                  <CheckListItemComponent checklistItem={checklistItem} itemKey={itemKey} key={itemKey} />
                ))}
            </div>
            {progress === 100 && <CompletedChest handleClick={handleChestClick} />}
            <button
              className={`text-purple px-4 pt-1 pb-4 text-xs mx-auto w-full ${checklistStyles['tap-effect-none']}`}
              onClick={handleExpand}
            >
              {t('checklists.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
